import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "./PublicLayout";
import { Wrench, Phone, ClipboardCheck, Package, Users, ArrowRight, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Wrench,
    n: "01",
    title: "Custom Hose Assembly",
    body: "Send us a drawing or a broken sample — we'll reverse-engineer, cut, crimp and pressure-test to spec. Made-to-order assemblies with your part number stamped on the ferrule.",
    features: ["Made-to-drawing", "Sample matching", "Part-number stamping", "48-hr dispatch"],
    span: "lg:col-span-4 lg:row-span-2",
    img: "https://images.pexels.com/photos/36532643/pexels-photo-36532643.jpeg?auto=compress&cs=tinysrgb&w=1400",
  },
  {
    icon: Phone,
    n: "02",
    title: "On-Site Emergency Service",
    body: "Site crews on standby for mines, ports and plants. When your line ruptures, we replace it — day or night.",
    features: ["24/7 coverage", "Field crews", "Rapid response"],
    span: "lg:col-span-2",
    variant: "red",
  },
  {
    icon: ClipboardCheck,
    n: "03",
    title: "Hose Testing & Certification",
    body: "Hydrostatic and burst testing to SAE / EN standards. Every assembly leaves with a test certificate and batch traceability code.",
    features: ["Hydrostatic test", "Test certificates", "Batch traceability"],
    span: "lg:col-span-2",
  },
  {
    icon: Package,
    n: "04",
    title: "Bulk Supply for OEMs",
    body: "Long-term supply contracts with negotiated pricing, dedicated inventory, and predictable dispatch windows for your production line.",
    features: ["Volume pricing", "Dedicated stock", "Kanban replenishment"],
    span: "lg:col-span-3",
    img: "https://images.pexels.com/photos/10733800/pexels-photo-10733800.jpeg?auto=compress&cs=tinysrgb&w=1400",
  },
  {
    icon: Users,
    n: "05",
    title: "Technical Consultation",
    body: "Not sure which hose or fitting your application needs? Our engineers help you spec the right assembly the first time.",
    features: ["Spec advice", "Failure analysis", "Route optimisation"],
    span: "lg:col-span-3",
  },
];

export default function Services() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 opacity-25">
          <img src="https://images.pexels.com/photos/12951623/pexels-photo-12951623.jpeg?auto=compress&cs=tinysrgb&w=2000" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#E11D2A] mb-6">— What We Do</div>
          <h1 className="font-industrial uppercase tracking-tighter text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-white max-w-5xl" data-testid="services-h1">
            More than<br /> a supplier.<br /> <span className="text-[#E11D2A]">A partner.</span>
          </h1>
          <p className="mt-8 text-white/70 text-lg max-w-2xl">
            Five service lines that go beyond the box — from custom assembly to 2 AM breakdowns at your job site.
          </p>
        </div>
      </section>

      {/* Bento grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="services-grid">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-3">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isRed = s.variant === "red";
            return (
              <div
                key={s.title}
                className={`relative overflow-hidden border ${isRed ? "border-[#E11D2A] bg-[#E11D2A] text-black" : "border-white/10 bg-[#0a0a0a] hover:border-[#E11D2A]"} transition-colors group ${s.span}`}
                data-testid={`service-${s.n}`}
              >
                {s.img && (
                  <>
                    <div className="absolute inset-0 opacity-30">
                      <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/60 to-black" />
                  </>
                )}
                <div className="relative p-8 md:p-10 h-full flex flex-col">
                  <div className="flex items-start justify-between mb-8">
                    <div className={`font-tech text-[10px] uppercase tracking-[0.3em] ${isRed ? "text-black/60" : "text-white/40"}`}>{s.n} / {String(SERVICES.length).padStart(2, "0")}</div>
                    <Icon size={28} strokeWidth={1.5} className={isRed ? "text-black" : "text-[#E11D2A]"} />
                  </div>
                  <div className={`font-industrial uppercase tracking-tight text-3xl md:text-4xl leading-none mb-4 ${isRed ? "text-black" : "text-white"}`}>{s.title}</div>
                  <p className={`text-sm leading-relaxed mb-6 ${isRed ? "text-black/70" : "text-white/60"}`}>{s.body}</p>
                  <div className="mt-auto flex flex-wrap gap-2">
                    {s.features.map((f) => (
                      <span key={f} className={`px-3 py-1 border font-tech text-[9px] uppercase tracking-[0.2em] ${isRed ? "border-black/20 text-black/80" : "border-white/15 text-white/70"}`}>{f}</span>
                    ))}
                  </div>
                  {isRed && (
                    <a href="tel:+918962913616" className="mt-6 inline-flex items-center gap-2 font-tech text-xs uppercase tracking-[0.25em] text-black hover:underline" data-testid="service-emergency-tel">
                      Call now <ArrowUpRight size={14} />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Process */}
      <section className="border-t border-white/10 bg-[#080808]" data-testid="services-process">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#E11D2A] mb-6">— How we work</div>
          <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-6xl text-white leading-none mb-14">The 5-step assembly.</h2>

          <div className="grid md:grid-cols-5 gap-0 border border-white/10">
            {[
              ["Enquire", "Send drawing / sample / PO"],
              ["Quote", "Priced in 24 hours"],
              ["Assemble", "Cut, crimp, mark"],
              ["Test", "Hydrostatic + certify"],
              ["Dispatch", "Ship with traceability"],
            ].map(([step, desc], i) => (
              <div key={step} className="p-8 border-b md:border-b-0 md:border-r last:border-r-0 border-white/10" data-testid={`process-${i + 1}`}>
                <div className="font-industrial text-6xl text-[#E11D2A] leading-none mb-4">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-industrial uppercase text-2xl text-white tracking-tight mb-2">{step}</div>
                <div className="font-tech text-[11px] uppercase tracking-[0.2em] text-white/50">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" data-testid="services-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-5xl text-white leading-none">
            Ready when you are.<br /><span className="text-[#E11D2A]">Let's get started.</span>
          </h2>
          <Link to="/contact" className="inline-flex items-center gap-3 px-7 py-4 bg-[#E11D2A] hover:bg-[#C81622] transition-colors font-tech text-xs uppercase tracking-[0.25em]" data-testid="services-cta-link">
            Start an Enquiry <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
